/**
 * Signed-blob refresh helpers for post image carousels.
 * Keeps `data-ps-carousel-slides` JSON in sync with refreshed img/video URLs.
 */

export const CAROUSEL_SLIDES_UPDATED_EVENT = 'blob-refresh:carousel-slides-updated';

/** DOM attributes for refresh_signed_blob_urls.js on a slide's media element. */
export function slideBlobDomProps(slide) {
  if (!slide) return {};
  const props = {};
  if (slide.signedId) {
    props['data-blob-signed-id'] = String(slide.signedId);
    props['data-blob-attrs'] = 'src';
    props['data-blob-disposition'] = 'inline';
  }
  if (slide.type === 'video' && slide.posterSignedId) {
    props['data-blob-poster-signed-id'] = String(slide.posterSignedId);
  }
  return props;
}

export function parseCarouselSlidesJson(raw) {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function findCarouselRoot(element) {
  return element?.closest?.('[data-post-carousel]') || null;
}

export function carouselSlideIndex(element) {
  const slideEl = element?.closest?.('.post-content-carousel__slide');
  if (!slideEl) return null;
  const idx = parseInt(slideEl.getAttribute('data-carousel-index'), 10);
  return Number.isFinite(idx) ? idx : null;
}

/**
 * After a slide media URL is refreshed on a DOM element, mirror it into the
 * carousel JSON attribute and notify the editor node view (if any).
 */
export function syncCarouselSlideUrlAfterRefresh(element, attr, url, signedId = null) {
  const carouselRoot = findCarouselRoot(element);
  if (!carouselRoot) return;

  const idx = carouselSlideIndex(element);
  if (idx === null) return;

  const slides = parseCarouselSlidesJson(carouselRoot.getAttribute('data-ps-carousel-slides'));
  if (!slides[idx]) return;

  const slide = { ...slides[idx] };
  let changed = false;

  if (attr === 'src' || attr === 'data-src') {
    if (slide.src !== url) {
      slide.src = url;
      changed = true;
    }
    if (signedId && slide.signedId !== signedId) {
      slide.signedId = signedId;
      changed = true;
    }
  } else if (attr === 'poster' || attr === 'data-poster') {
    if (slide.poster !== url) {
      slide.poster = url;
      changed = true;
    }
    if (signedId && slide.posterSignedId !== signedId) {
      slide.posterSignedId = signedId;
      changed = true;
    }
  }

  if (!changed) return;

  const nextSlides = slides.map((s, i) => (i === idx ? slide : s));
  carouselRoot.setAttribute('data-ps-carousel-slides', JSON.stringify(nextSlides));

  document.dispatchEvent(
    new CustomEvent(CAROUSEL_SLIDES_UPDATED_EVENT, {
      bubbles: true,
      detail: { carouselRoot, slides: nextSlides },
    })
  );
}
